import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import { Box } from "@mui/material";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import CompanyDetailsDrawer from "@components/CompanyDetailsDrawer/CompanyDetailsDrawer";
import CompanyDisplay from "@components/CompanyDetailsDrawer/CompanyDisplay";
import CompanyFilterDrawer from "@components/FilterControls/CompanyFilterDrawer";
import TableEmptyState from "@components/Table/TableEmptyState";
import {
  TableToolbar,
  TableToolbarDropdown,
  TableToolbarDropdownMenuItem,
  TableToolbarFilterButton,
  TableToolbarLeft,
  TableToolbarRight,
  TableToolbarTitle,
} from "@components/TableToolbar/TableToolbar";

import { GetChatRequestType } from "@interfaces/chat.type";
import { CompanyType } from "@interfaces/company.type";
import { ITechnology } from "@interfaces/portfolio.type";

import CompaniesTable from "@pages/Companies/components/CompaniesTable";
import useTechnologyCompanySearchToolbar from "@pages/Technology/hooks/useTechnologyCompanySearchToolbar";
import useTechnologyCompanyTableConfig from "@pages/Technology/hooks/useTechnologyCompanyTableConfig";

import { useFetchCompanyInsight, useTechnologyQuery } from "@queries/portfolio.query";

import CompanyService from "@services/company.service";

import { useTableStore } from "@store/filters/filters.store";
import { INITIAL_FILTERS_STATES_MAP } from "@store/filters/initialStates";
import { useOrganizationStore } from "@store/organizationStore";

import contentCardStyles from "@theme/contentCardStyles";

import numberOfActiveFilters from "@utilities/numberOfActiveFilters";

export default function TechnologyCompanies() {
  const [focusedId, setFocusedId] = useState<null | string>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const organization = useOrganizationStore((state) => state.organization);
  const modelUsed = (organization?.chatModel || "openai") as GetChatRequestType["model"];
  const { data: insightData, mutate: fetchInsight, status: insightStatus } = useFetchCompanyInsight();

  const isInsightLoading = insightStatus === "pending";

  const { filters, pagination, selection, setTablePagination, setTableSelection, setTableSort, sort } =
    useTableStore("technologyCompany");

  const parameters = useParams();
  const technologyId = parameters.technologyId;

  const { data: technology, status: technologyStatus } = useTechnologyQuery(technologyId);

  useEffect(() => {
    if (technology && technology.companies.length > 0) {
      const companiesContainScores = _.some(technology.companies, "similarityScore");
      setTablePagination({
        page: 0,
        pageSize: 20,
      });
      setTableSort([{ field: companiesContainScores ? "similarityScore" : "numEmployees", sort: "desc" }]);
    }
  }, [technology]);

  const { companies } = technology as ITechnology;

  const filteredCompanies = useMemo<CompanyType[]>(
    () => CompanyService.filterCompanies(companies, { filters, pagination, selection, sort }),
    [companies, filters, pagination, sort, selection, isInsightLoading],
  );

  const focusedCompany = filteredCompanies.find((company) => company.id === focusedId);

  const { handleAddCompaniesToTechnology, handleRemoveCompanies, toggleFilterVisibility } =
    useTechnologyCompanySearchToolbar({
      allCompanies: filteredCompanies,
      isFilterOpen,
      selectedIds: selection,
      setIsFilterOpen,
      technologyId: technologyId!,
      technologyName: technology?.name ?? "",
      workflow: "portfolio",
    });

  const companyTableRowMenuItems = useMemo(
    () => [
      {
        func: (companyId: string) => void handleRemoveCompanies(companyId),
        label: "Remove from Technology",
      },
    ],
    [handleRemoveCompanies],
  );

  const { columns, handleRowFocus, initialState, rows } = useTechnologyCompanyTableConfig({
    companies: filteredCompanies,
    rowMenuItems: companyTableRowMenuItems,
    setFocusedId,
  });

  if (!companies || companies.length === 0) {
    return (
      <TableEmptyState
        handleButtonClick={() => void 0}
        heading="Build Your Company List"
        resourceLink="/"
        state="companies"
        supportingText="Discover and connect with innovative companies."
      />
    );
  }

  const getInsight = (companyId: string) => {
    let messages: string[] | undefined;
    if (focusedCompany?.insight) {
      messages = Array.isArray(focusedCompany.insight) ? focusedCompany.insight : [focusedCompany.insight];
    }
    fetchInsight({
      companyId,
      messages,
      model: modelUsed,
      searchId: focusedCompany?.embeddingSearchId ?? "new",
      technologyId: technologyId!,
    });
  };

  return (
    <Box
      sx={{
        ...contentCardStyles,
        // needed to prevent double scrollbars
        maxHeight: "calc(100vh - 186px)",
      }}
    >
      <CompanyDetailsDrawer close={() => setFocusedId(null)} isOpen={focusedId !== null}>
        {focusedCompany && (
          <CompanyDisplay
            close={() => setFocusedId(null)}
            company={focusedCompany}
            getInsight={getInsight}
            isInsightLoading={isInsightLoading}
          />
        )}
      </CompanyDetailsDrawer>
      <CompanyFilterDrawer filterKey="technologyCompany" isOpen={isFilterOpen} setOpen={setIsFilterOpen} />
      <>
        <TableToolbar>
          <TableToolbarLeft>
            <TableToolbarTitle
              resourceLink=""
              tableSubtitle="Search by name or filter by industry, location, and more."
              tableTitle="Companies"
              workflow="portfolio"
            />
          </TableToolbarLeft>
          <TableToolbarRight>
            <TableToolbarFilterButton
              activeFilters={numberOfActiveFilters(filters, INITIAL_FILTERS_STATES_MAP["technologyCompany"]["filters"])}
              toggleFilterVisibility={toggleFilterVisibility}
              tooltipText="Filter companies based on location, industry, and more."
            />
            <TableToolbarDropdown
              actionsLabel="Actions"
              selectedIds={selection}
              tooltipText="Save selected companies to your Portfolio or download to CSV."
            >
              <TableToolbarDropdownMenuItem onClick={handleAddCompaniesToTechnology}>
                <ListItemIcon>
                  <LightbulbRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Save To Technology</ListItemText>
              </TableToolbarDropdownMenuItem>
              <TableToolbarDropdownMenuItem onClick={() => handleRemoveCompanies()}>
                <ListItemIcon>
                  <DeleteForeverRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Remove Companies</ListItemText>
              </TableToolbarDropdownMenuItem>
              <TableToolbarDropdownMenuItem onClick={() => CompanyService.exportCompanies(selection, companies)}>
                <ListItemIcon>
                  <DownloadRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export Company Data</ListItemText>
              </TableToolbarDropdownMenuItem>
            </TableToolbarDropdown>
          </TableToolbarRight>
        </TableToolbar>
        <CompaniesTable
          columns={columns}
          handlePaginationModelChange={setTablePagination}
          handleRowFocus={handleRowFocus}
          handleSortModelChange={setTableSort}
          initialState={initialState}
          isCompanySearching={technologyStatus === "pending"}
          pagination={pagination}
          paginationMode="client"
          rows={rows}
          selection={selection}
          setSelection={setTableSelection}
          sortingMode="client"
          sortModel={sort}
        />
      </>
    </Box>
  );
}
